import React, { useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { Loader2, Trash2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useI18n } from '../../contexts/I18nContext';

interface Comment {
  id: number;
  content: string;
  created_at: string;
  user_id: string;
  profile: {
    username: string;
    avatar_url?: string;
  };
}

interface CommentSectionProps {
  resourceId: string;
  resourceType: 'deck' | 'trade';
  comments: Comment[];
  onCommentAdded: () => void;
  className?: string;
  apiKey?: string;
}

export function CommentSection({ resourceId, resourceType, comments, onCommentAdded, className, apiKey }: CommentSectionProps) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ];

  const handleSubmit = useCallback(async () => {
    if (!user || !content.trim() || submitting) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from(`${resourceType}_comments`)
        .insert([
          {
            [`${resourceType}_id`]: resourceId,
            content: content.trim(),
            user_id: user.id
          }
        ]);

      if (error) throw error;

      setContent('');
      onCommentAdded();
    } catch (error) {
      console.error(t('comments.error_adding'), error);
    } finally {
      setSubmitting(false);
    }
  }, [user, content, resourceType, resourceId, onCommentAdded, submitting, t]);

  const handleDeleteComment = useCallback(async (commentId: number) => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from(`${resourceType}_comments`)
        .delete()
        .eq('id', commentId);

      if (error) throw error;
      onCommentAdded();
    } catch (error) {
      console.error(t('comments.error_deleting'), error);
    } finally {
      setIsDeleting(false);
    }
  }, [resourceType, onCommentAdded, isDeleting, t]);

  return (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden ${className || ''}`}>
      <div className="border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 px-6 py-4">
          {t('comments.title')} ({comments.length})
        </h3>
      </div>

      <div className="divide-y divide-gray-200">
        {/* Liste des commentaires */}
        {comments.map((comment) => (
          <div key={comment.id} className="p-6">
            <div className="flex space-x-3">
              <div className="flex-shrink-0">
                {comment.profile.avatar_url ? (
                  <img
                    className="h-10 w-10 rounded-full"
                    src={comment.profile.avatar_url}
                    alt={comment.profile.username}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 font-medium">
                      {comment.profile.username.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">
                    {comment.profile.username}
                  </h3>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-500">
                      {formatDistanceToNow(new Date(comment.created_at), {
                        addSuffix: true,
                        locale: lang === 'fr' ? fr : enUS
                      })}
                    </p>
                    {user?.id === comment.user_id && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-gray-400 hover:text-red-600"
                        disabled={isDeleting}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div 
                  className="text-sm text-gray-500 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: comment.content }}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Formulaire de nouveau commentaire */}
        {user ? (
          <div className="p-6">
            <div className="mt-4">
              <ReactQuill
                value={content}
                onChange={setContent}
                modules={modules}
                formats={formats}
                placeholder={t('comments.write_comment')}
                className="bg-white rounded-lg"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={submitting || !content.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {t('comments.submit')}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 bg-gray-50 text-center">
            <p className="text-sm text-gray-500">
              {t('comments.login_required')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}